import React from 'react';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type NestedBarChartProps = {
  id: string;
  data: types.widgets.TableData;
  count?: types.widgets.CountData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };

  tooltipText?: string;
  hideLegend?: boolean;
  hideSeries?: string[];
  numberFormat?: string | null;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  category?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  rotation?: number;
  setExternalEmbed?: (_data: types.analysis.EmbedChartData) => void;
  seriesOrder?: (_c: string[]) => string[];
  forceColor?: Record<string, string>;
  displayNumbers?: boolean;
  stacked?: boolean;
  negativeColor?: string;
  positiveColor?: string;
  horizontal?: boolean;
  colorByBar?: boolean;
  paletteVersion?: 'v1' | 'v2';
  minAxisValue?: number;
};

const NestedBarChart = (args: NestedBarChartProps) => {
  const uiStyles = useUiStyles();

  const {
    id,
    data,
    count,
    fullScreen,
    readOnly,
    alert,
    tooltipText = '[bold]{name}:[/] {valueY}',
    hideSeries,
    hideLegend,
    category = 'history',
    numberFormat = '#.##',
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    yAxisLabel,
    xAxisLabel,
    rotation = undefined,
    seriesOrder = (c: string[]) => c,
    forceColor = {},
    displayNumbers = false,
    stacked = false,
    negativeColor = undefined,
    positiveColor = undefined,
    horizontal = false,
    colorByBar = false,
    paletteVersion = 'v2',
    minAxisValue = undefined,
  } = args;

  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'nested-bar-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });

      setLocalData(data);
    };

    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              count={count}
              stacked={stacked}
              fullScreen={fullScreen}
              numberFormat={numberFormat}
              download={download}
              rotation={rotation}
              yAxisLabel={yAxisLabel}
              xAxisLabel={xAxisLabel}
              category={category}
              hideSeries={hideSeries}
              hideLegend={hideLegend}
              tooltipText={tooltipText}
              seriesOrder={seriesOrder}
              forceColor={forceColor}
              displayNumbers={displayNumbers}
              negativeColor={negativeColor}
              positiveColor={positiveColor}
              horizontal={horizontal}
              colorByBar={colorByBar}
              paletteVersion={paletteVersion}
              minAxisValue={minAxisValue}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default NestedBarChart;
